/** @format */

import { i18nContext } from '@/context/i18nContext';
import { FunctionComponent as FC, useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import 'swiper/css';
import 'swiper/css/navigation';
import { Keyboard } from 'swiper/modules';
import ChevronLeft from '../../assets/icons/arrow_left.svg';
import ChevronRight from '../../assets/icons/arrow_right.svg';
import { breakpointFrom } from '../../theme/mixins';
import { Icon } from '../ui/Icon';
import { H4 } from '../ui/Typography';

import { Swiper, SwiperSlide } from 'swiper/react';

export const Slider: FC<Props> = ({ slides, withCounter = false }) => {
  const swiperRef = useRef<any>(null);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [activeSlide, setActiveSlide] = useState(1);
  const [currentClassName, setCurrentClassName] = useState('forward');

  const handlePrevious = useCallback(() => {
    swiperRef.current?.swiper.slidePrev();
    setCurrentClassName('backward');
  }, [swiperRef]);

  const handleNext = useCallback(() => {
    swiperRef.current?.swiper.slideNext();
    setCurrentClassName('forward');
  }, [swiperRef]);

  useEffect(() => {
    swiperRef.current?.swiper.on('slideChange', () => {
      setIsFirstSlide(swiperRef.current?.swiper.isBeginning);
      setIsLastSlide(swiperRef.current?.swiper.activeIndex === slides.length - 1);
      setActiveSlide(swiperRef.current?.swiper.activeIndex + 1);
    });
    swiperRef.current?.swiper.on('slideNextTransitionStart', () => {
      setCurrentClassName('forward');
    });
    swiperRef.current?.swiper.on('slidePrevTransitionStart', () => {
      setCurrentClassName('backward');
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (swiperRef.current) {
        swiperRef.current.swiper.update(); // Update Swiper when the window is resized
      }
    };

    if (swiperRef.current) {
      swiperRef.current.swiper.update(); // Initial update
      window.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <i18nContext.Consumer>
      {t => (
        <StyledSlider>
          <Swiper
            speed={600}
            updateOnWindowResize={true}
            ref={swiperRef}
            breakpoints={breakpoints}
            keyboard={{ enabled: true }}
            modules={[Keyboard]}
            allowSlidePrev={!isFirstSlide}
            allowSlideNext={!isLastSlide}
            spaceBetween={16}
          >
            {slides.map(({ key, el }) => (
              <SwiperSlide className={currentClassName} key={key}>
                {el}
              </SwiperSlide>
            ))}
            <SwiperSlide></SwiperSlide>
          </Swiper>
          <div className="controls">
            <button onClick={handlePrevious} title={t['aria-label']['previousSlide']} disabled={isFirstSlide}>
              <Arrow icon={ChevronLeft} width={44} height={44} />
            </button>
            {withCounter && (
              <H4 as="span">
                {activeSlide}/{slides.length}
              </H4>
            )}
            <button onClick={handleNext} title={t['aria-label']['nextSlide']} disabled={isLastSlide}>
              <Arrow icon={ChevronRight} width={44} height={44} />
            </button>
          </div>
        </StyledSlider>
      )}
    </i18nContext.Consumer>
  );
};

const breakpoints = {
  0: {
    slidesPerView: 1.25,
  },
  768: {
    slidesPerView: 1.8,
  },
  1440: {
    slidesPerView: 1.8,
  },
  1441: {
    slidesPerView: 2,
  },
};

const StyledSlider = styled.div`
  .controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;

    ${breakpointFrom.md`
      margin-right: 48%;
    `}
  }

  .controls span {
    color: ${({ theme }) => theme.color.teal.light};
  }

  button {
    display: grid;
    place-content: center;
    border-radius: 50%;
    cursor: pointer;
    border: none;
    background-color: ${({ theme }) => theme.color.white};
  }

  .swiper-wrapper {
    padding-left: 10px;
    transition: transform 1.5s ease-in-out;
  }

  .swiper-slide:not(.swiper-slide-active) {
    transform: scale(0.8);
    transition: transform 1s;
  }

  .swiper-slide:first-of-type.forward {
    animation: none !important;
  }

  .swiper-slide:not(.swiper-slide-active).backward {
    animation: sizeUp 0.5s;
    animation-delay: 0s;
    animation-direction: reverse;
  }

  .swiper-slide-active.forward {
    animation: bounce 1s, sizeUp 0.6s;
    animation-delay: 0.1s, 0s;
  }

  .swiper-slide-active.backward {
    animation: bounceBackward 1s;
    animation-delay: 0.1s;
  }

  @keyframes sizeUp {
    0% {
      transform: scale(0.8);
    }

    100% {
      transform: scale(1);
    }
  }

  @keyframes bounce {
    0% {
      transform: translateX(-18px);
    }

    100% {
      transform: translateX(0);
    }
  }

  @keyframes bounceBackward {
    0% {
      transform: translateX(18px);
    }

    100% {
      transform: translateX(0);
    }
  }
`;

const Arrow = styled(Icon)`
  mask-size: 200%;
  mask-position: center;
`;

type Props = {
  slides: {
    el: React.ReactNode;
    key: string;
  }[];
  withCounter?: boolean;
};
